// navigation-toggle.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * @service NavigationToggleService
 * @description Service responsible for managing the expansion and collapse state of the application's main navigation menu (sidebar).
 *
 * This service acts as a central communication bridge between the navigation trigger (usually in the header/toolbar)
 * and the layout components (sidebar, main content area). It utilizes RxJS to provide a reactive stream of state changes,
 * allowing the UI to adjust dynamically without tight coupling between components.
 *
 * **Key Responsibilities:**
 * 1. **State Holding**: Maintains the current boolean state of the menu (collapsed vs. expanded).
 * 2. **State Broadcasting**: Notifies subscribers (SidebarComponent, MainLayoutComponent) whenever the state changes.
 * 3. **State Mutation**: Provides a method to toggle the state safely.
 *
 * **Usage Scenario:**
 * - A user clicks the "hamburger" icon in the top navigation bar.
 * - The header component calls `toggleMenu()`.
 * - The `collapsedState` BehaviorSubject updates its value.
 * - The `isCollapsed$` observable emits the new value.
 * - The Sidebar component receives the value and adds/removes CSS classes to shrink/expand.
 * - The Main Content component receives the value and adjusts its left margin.
 *
 * @feature 1. Reactive state management using RxJS BehaviorSubject.
 * @feature 2. Singleton instance provided at the root level.
 * @feature 3. Simple toggle API for ease of use.
 *
 * @architecture
 * Uses a `private BehaviorSubject` to store state and a `public Observable` to expose it.
 * This encapsulation ensures that the state can only be modified through the defined `toggleMenu` method,
 * adhering to unidirectional data flow principles.
 *
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationToggleService {
  /**
   * @private
   * @type {BehaviorSubject<boolean>}
   * @description Internal subject holding the current collapsed state.
   * Initialized to `false` (meaning the menu is expanded by default).
   * @default false
   */
  private collapsedState = new BehaviorSubject<boolean>(false);

  /**
   * @public
   * @type {Observable<boolean>}
   * @description Public observable stream that emits the current collapsed state.
   * Components should subscribe to this to react to menu toggles.
   * - `true`: Menu is collapsed (small width/hidden labels).
   * - `false`: Menu is expanded (full width).
   */
  isCollapsed$ = this.collapsedState.asObservable();

  /**
   * @method toggleMenu
   * @description Toggles the navigation menu state between collapsed and expanded.
   *
   * Retrieves the current value from the `collapsedState` subject, inverts it (true -> false, false -> true),
   * and emits the new value to all subscribers.
   *
   * @returns {void}
   * @side-effect Updates `collapsedState` and triggers emission on `isCollapsed$`.
   *
   * @example
   * // In a HeaderComponent template or class
   * onMenuButtonClick() {
   *   this.navigationToggleService.toggleMenu();
   * }
   */
  toggleMenu() {
    this.collapsedState.next(!this.collapsedState.value);
  }
}
